import { Component, TemplateFunction } from '@ribajs/core'
import { throttle, debounce } from '@ribajs/utils'
import { Pjax } from '@ribajs/router'
import {
  ShopifyProductService,
  ShopifyCollectionService,
  CustomerFavoritesService,
} from '../services/index.js'
import { getDiffInSeconds, isInViewport } from '../utils/index.js'
import { TwSlideshowComponent } from '../modules/tailwind/components/index.js'
import { SbSidebarComponent } from './sb-sidebar.component.js'
import { SbVariantSelectorComponent } from './sb-variant-selector.component.js'

import type { SbPopupComponent } from './sb-popup.component.js'
import type {
  CardProductOptionValue,
  LineItemProperties,
  SidebarState,
  VariantOverlayData,
  ScopeSbCardProduct,
} from '../types/index.js'

const AUTO_UPDATE_INTERVAL_IN_SECONDS = 600
export class SbCardProductComponent extends Component {
  public static tagName = 'sb-card-product'

  /** True if data is loaded */
  protected dataLoaded = false
  protected dataIsLoading = false

  /** The parent `tw-slideshow`, only defined if the card-product is inside a slideshow */
  protected parentSlideshow: TwSlideshowComponent | null = null

  /** The parent `sb-sidebar` which also includes the off canvas cart, only defined if the card-product is inside a sidebar, for example the cart product recommendations */
  protected parentSidebar: SbSidebarComponent | null = null

  /** The child `sb-variant-selector` to select the variant */
  public childVariantSelector: SbVariantSelectorComponent | null = null

  protected variantOverlayPopup?: SbPopupComponent

  /**
   * - handle is the product handle to get the product json object
   * - product is the product object itself if you want to pass it directly
   */
  static get observedAttributes(): string[] {
    return [
      'handle',
      'variant-id',
      'allowed-variant-ids',
      'product',
      'collection-handle',
      'is-recommendation',
      'is-favorite',
      'is-free-present',
      'load-in-viewport',
      'hide-variant-selector',
      'quantity',
      'order-quantity-limit',
      'allow-trusted-shop-widgets',
    ]
  }

  protected requiredAttributes(): string[] {
    return ['handle']
  }

  /**
   * Called as soon as this custom element es registered as a available custom element
   */
  constructor() {
    super()

    // Bind event listeners to `this`, so we can easily pass them to DOM event handlers and remove them again later
    this.onResize = throttle(this.onResize.bind(this)) as typeof this.onResize
    this.onScroll = debounce(this.onScroll.bind(this)) as typeof this.onScroll
    this.onSlide = this.onSlide.bind(this)
    this.onSidebarStateChanged = this.onSidebarStateChanged.bind(this)
    this.onImageLoaded = this.onImageLoaded.bind(this)
    this.onMouseOver = this.onMouseOver.bind(this)
    this.onVariantChanged = this.onVariantChanged.bind(this)
  }

  public scope: ScopeSbCardProduct = {
    handle: undefined,
    hideVariantSelector: false,
    collectionHandle: undefined,
    isRecommendation: false,
    isFreePresent: false,
    isFavorite: false,
    product: undefined,
    variant: undefined,
    featured_image: {},
    featuredImageEl: null,
    quantity: 1,
    orderQuantityLimit: -1,
    showSpinner: true,
    optionSelected: false,
    allowTrustedShopWidgets: false,

    badges: {
      new: false,
      sale: 0,
      saleSum: 0,
      soldOut: false,
      outOfStockText: '',
      discountInfo: '',
      additionalInfo: '',
    },

    loadInViewport: true,

    metafields: null,

    goToProduct: this.goToProduct,

    onImageLoaded: this.onImageLoaded,

    showVariantOverlayPopup: this.showVariantOverlayPopup,

    ratingTemplate: '',
  }

  protected onProductChanged() {
    this.scope.handle = this.scope.handle || this.scope.product?.handle
  }

  /**
   * Split options to `values` (max 3), more (the other more than 3) and all
   * @param optionValues
   * @returns
   */
  protected splitCardOption(optionValues: CardProductOptionValue[]) {
    const three: CardProductOptionValue[] = []
    const more: CardProductOptionValue[] = []
    // Split options to three and more
    if (optionValues.length > 3) {
      three.push(...optionValues.slice(0, 3))
      more.push(...optionValues.slice(3))
    } else {
      three.push(...optionValues)
    }
    return {
      original: optionValues,
      three,
      more,
    }
  }

  protected updateTrustedShopRatingTemplates() {
    const oldBarcode = this.scope.currentBarcode
    const newBarcode = this.scope.variant?.barcode
    const barcodeChanged = oldBarcode !== newBarcode

    if (!barcodeChanged) {
      return
    }

    this.scope.currentBarcode = this.scope.variant?.barcode

    // Only reload the rating template if a barcode is set
    if (this.scope.variant?.barcode) {
      // This is a WORKAROUND for the `etrusted-widget` to force template reloading
      const ratingTemplates = ShopifyProductService.getRatingTemplates(
        'text-xs font-body',
        this.scope.currentBarcode,
      )

      // Set to empty string to force an template update
      if (this.scope.ratingTemplate.length > 0) this.scope.ratingTemplate = ''
      this.scope.ratingTemplate = ratingTemplates.small
    } else {
      if (this.scope.ratingTemplate.length > 0) this.scope.ratingTemplate = ''
    }
  }

  protected onVariantChanged() {
    if (this.scope.allowTrustedShopWidgets) {
      this.updateTrustedShopRatingTemplates()
    }

    const { badges, featuredImage } =
      ShopifyProductService.getPreparedProductVariantData(
        this.scope.product!,
        this.scope.variant!,
        this.scope.metafields,
      )
    this.scope.badges = badges
    this.scope.featured_image = featuredImage

    if (this.scope.isFavorite) {
      const handle = this.scope.handle || this.scope.product?.handle
      if (!handle) {
        console.error(
          new Error('[SbCardProductComponent.setVariant] No handle found!'),
        )
        return
      }
      if (!this.scope.variant) {
        console.error(
          new Error(
            `[SbCardProductComponent.setVariant] No variant found for product handle "${handle}"`,
          ),
        )
        return
      }
      CustomerFavoritesService.updateProduct(handle, this.scope.variant.id)
    }
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbCardProductComponent.observedAttributes)
    this.addEventListeners()
  }

  /**
   * Called as soon as this element is removed from the DOM
   */
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  /**
   * Additional line item cart properties, used for tracking
   * @returns
   */
  protected getProperties() {
    const cartProperties: LineItemProperties = {}
    if (this.scope.collectionHandle) {
      cartProperties._collection = this.scope.collectionHandle
    }
    if (this.scope.parentCollectionHandle) {
      cartProperties._parentCollection = this.scope.parentCollectionHandle
    }

    cartProperties._isRecommendation = this.scope.isRecommendation
    cartProperties._isFavorite = this.scope.isFavorite
    cartProperties._isFreePresent = this.scope.isFreePresent
    cartProperties._orderQuantityLimit = this.scope.orderQuantityLimit

    return cartProperties
  }

  /** Get href / url of the product detail page */
  public getPdpUrl() {
    if (!this.scope.handle) {
      throw new Error('Product handle not set!')
    }

    const pdpHref =
      this.querySelector<HTMLAnchorElement>('a.product-pdp-href')?.href

    if (pdpHref) {
      return new URL(pdpHref, window.location.origin)
    }

    return ShopifyProductService.getProductUrl(
      this.scope.handle /*, this.scope.collectionHandle*/,
    )
  }

  /** Prefetch PDP for faster page load */
  public async prefetchPDP() {
    if (window.router_enable) {
      const pdpUrl = this.getPdpUrl()
      await Pjax.getInstance()?.prefetchLink(pdpUrl.href)
    }
  }

  public async goToProduct() {
    const pdpUrl = this.getPdpUrl()

    if (window.router_enable) {
      Pjax.getInstance()?.goTo(pdpUrl.href)
    } else {
      window.location.href = pdpUrl.href
    }

    return
  }

  public showVariantOverlayPopup(event?: Event) {
    if (!this.variantOverlayPopup) {
      this.error(`Popup with id "sb-popup-variant-overlay" not found!`)
      return
    }
    event?.preventDefault()
    event?.stopPropagation()
    const variantOverlayData = this.getVariantOverlayData()
    this.variantOverlayPopup.show(event, variantOverlayData)

    this.variantOverlayPopup.addEventListener(
      'state-changed',
      this.onVariantOverlayPopupHidden as any,
    )
  }

  protected _onVariantOverlayPopupHidden(
    event: CustomEvent<{ visible: boolean }>,
  ) {
    const visible = event.detail.visible
    if (!visible && this.variantOverlayPopup) {
      this.variantOverlayPopup.removeEventListener(
        'state-changed',
        this.onVariantOverlayPopupHidden as any,
      )
    }
  }

  onVariantOverlayPopupHidden = this._onVariantOverlayPopupHidden.bind(this)

  protected initVariantOverlayPopup() {
    if (this.variantOverlayPopup) {
      return
    }
    this.variantOverlayPopup =
      document.querySelector(`sb-popup[id='sb-popup-variant-overlay']`) ||
      undefined
    if (!this.variantOverlayPopup) {
      console.error(
        new Error(
          `sb-popup element with id "sb-popup-variant-overlay" not found!`,
        ),
      )
    }
  }

  /**
   * Updates the product data without cache to ensure that the product is up to date
   */
  protected async updateData() {
    const now = new Date()

    if (!this.scope.updatedAt) {
      return this.initData()
    }

    const pastSeconds = getDiffInSeconds(this.scope.updatedAt, now)

    if (pastSeconds < AUTO_UPDATE_INTERVAL_IN_SECONDS) {
      this.debug(`The last update was ${pastSeconds} seconds ago, do nothing`)
      return
    }

    // Make sure to restore the previously selected variant
    if (this.scope.variant?.id) {
      this.scope.variantId = this.scope.variant?.id
    }

    this.debug(
      `The last update was ${pastSeconds} seconds ago, start to update product data...`,
    )

    await this.updateProductData(false)

    this.scope.updatedAt = now

    this.updateCache()
  }

  protected async updateProductData(enableCache: boolean) {
    if (!this.scope.handle) {
      throw new Error('Product handle not set!')
    }
    const {
      product,
      metafields,
      badges,
      orderQuantityLimit,
      bgColor,
      featuredImage,
    } = await ShopifyProductService.getPreparedProductData(
      this.scope.handle,
      this.scope.variantId,
      enableCache,
      this.scope.allowedVariantIds,
    )
    this.scope.product = product
    this.scope.metafields = metafields
    this.scope.badges = badges
    this.scope.orderQuantityLimit = orderQuantityLimit
    this.scope.featured_image = featuredImage

    this.onProductChanged()

    ShopifyProductService.setImageBgColor(this, bgColor)
  }

  protected updateCache() {
    if (this.scope.product) {
      ShopifyProductService.cacheProduct(this.scope.product)
    }

    if (this.scope.metafields && this.scope.handle) {
      ShopifyProductService.cacheMetafields(
        this.scope.metafields,
        this.scope.handle,
      )
    }
  }

  /** First time we get the product data (from cache if found) */
  protected async initData() {
    // Do noting if data was already loaded
    if (this.dataLoaded || this.dataIsLoading) {
      return
    }

    if (!this.scope.handle) {
      throw new Error('Product handle not set!')
    }

    this.dataIsLoading = true

    try {
      await this.updateProductData(true)
    } catch (error) {
      this.innerHTML = `Error: ${(error as Error).message || error}`
      this.throw(error)
    }

    if (this.scope.product) {
      ShopifyProductService.cacheProduct(this.scope.product)
    }

    if (!this.scope.handle && !this.scope.product) {
      throw new Error('Product handle and product not set!')
    }

    this.querySelector('.sb-card-product-info')?.classList.remove('opacity-0')
    this.classList.add(`${SbCardProductComponent.tagName}-ready`)

    this.dataLoaded = true
    this.dataIsLoading = false

    this.scope.updatedAt = new Date()

    this.updateCache()
  }

  protected getVariantOverlayData() {
    /** Data to be passed via the popup to the variant selector component */
    const variantOverlayData: VariantOverlayData = {
      isRecommendation: false,
      isFavorite: false,
      quantity: 1,
      featured_image: {},
      productBgColor: '',
    }
    // handle
    if (this.scope.handle && this.scope.handle !== variantOverlayData.handle) {
      variantOverlayData.handle = this.scope.handle
    }
    // product
    if (
      this.scope.product &&
      this.scope.product !== variantOverlayData.product
    ) {
      variantOverlayData.product = this.scope.product
    }
    // variant
    if (
      this.scope.variant &&
      this.scope.variant !== variantOverlayData.variant
    ) {
      variantOverlayData.variant = this.scope.variant
    }
    // collectionHandle
    if (
      this.scope.collectionHandle &&
      this.scope.collectionHandle !== variantOverlayData.collectionHandle
    ) {
      variantOverlayData.collectionHandle = this.scope.collectionHandle
    }
    // isRecommendation
    if (
      this.scope.isRecommendation &&
      this.scope.isRecommendation !== variantOverlayData.isRecommendation
    ) {
      variantOverlayData.isRecommendation = this.scope.isRecommendation
    }
    // isFavorite
    if (
      this.scope.isFavorite &&
      this.scope.isFavorite !== variantOverlayData.isFavorite
    ) {
      variantOverlayData.isFavorite = this.scope.isFavorite
    }
    // parentCollectionHandle
    if (
      this.scope.parentCollectionHandle &&
      this.scope.parentCollectionHandle !==
        variantOverlayData.parentCollectionHandle
    ) {
      variantOverlayData.parentCollectionHandle =
        this.scope.parentCollectionHandle
    }
    // quantity
    if (
      this.scope.quantity &&
      this.scope.quantity !== variantOverlayData.quantity
    ) {
      variantOverlayData.quantity = this.scope.quantity
    }
    // featured_image
    if (
      this.scope.featured_image &&
      this.scope.featured_image !== variantOverlayData.featured_image
    ) {
      if (!this.scope.featured_image.src) {
        this.scope.featured_image = ShopifyProductService.getFeaturedImage(
          this.scope.product,
          this.scope.variant,
        )
      }
      variantOverlayData.featured_image = this.scope.featured_image
    }

    // productBgColor
    const bgColor = ShopifyProductService.getBgColorFromMetafields(
      this.scope.metafields,
    )

    if (bgColor && bgColor !== variantOverlayData.productBgColor) {
      variantOverlayData.productBgColor = bgColor
    }

    return variantOverlayData
  }

  protected async initDataIfInViewport() {
    // Do noting if data was already loaded
    if (this.dataLoaded || this.dataIsLoading) {
      return
    }

    if (isInViewport(this, { top: 500, bottom: 0, left: 300, right: 0 })) {
      await this.initData()

      // this.prefetchPDP()

      this.parentSlideshow?.removeEventListener('scrolling', this.onSlide)
      this.parentSidebar?.removeEventListener(
        'state-changed',
        this.onSidebarStateChanged,
      )
    }
  }

  protected async beforeBind() {
    await super.beforeBind()

    if (!this.scope.handle) {
      throw new Error('Product handle not set!')
    }

    if (this.scope.collectionHandle && !this.scope.parentCollectionHandle) {
      this.scope.parentCollectionHandle = ShopifyCollectionService.getParent(
        this.scope.collectionHandle,
      )
    }

    // If only one variant is allowed, this can be selected automatically (if no variant is selected directly)
    if (!this.scope.variantId && this.scope.allowedVariantIds?.length === 1) {
      this.scope.variantId = this.scope.allowedVariantIds[0]
    }

    if (!this.dataLoaded && !this.dataIsLoading) {
      if (!this.scope.loadInViewport) {
        await this.initData()
      } else {
        await this.initDataIfInViewport()
      }
    }

    this.setElements()
    this.addEventListeners()
    this.initVariantOverlayPopup()

    await this.onResize()
  }

  protected async afterBind() {
    this.classList.remove('opacity-0')
  }

  protected setElements() {
    if (!this.parentSlideshow)
      this.parentSlideshow = this.closest(TwSlideshowComponent.tagName)
    if (!this.parentSidebar)
      this.parentSidebar = this.closest(SbSidebarComponent.tagName)
    this.setChildVariantSelector()
  }

  protected setChildVariantSelector() {
    if (!this.childVariantSelector) {
      this.childVariantSelector = this.querySelector(
        SbVariantSelectorComponent.tagName,
      )
      if (this.childVariantSelector) {
        this.childVariantSelector.cardProductEls = [this]
      }
    }
  }

  /**
   * Adds / removes the class `card-product-xs` / 'card-product-md' depending on the size of the card itself
   */
  protected async onResize() {
    await this.initDataIfInViewport()

    if (this.clientWidth < 256) {
      this.classList.add('card-product-xs')
      this.classList.remove('card-product-md')
    } else {
      this.classList.add('card-product-md')
      this.classList.remove('card-product-xs')
    }
  }

  protected async onImageLoaded(eventName: 'changed' | 'loaded') {
    if (eventName === 'loaded') {
      this.scope.showSpinner = false
    } else {
      this.scope.showSpinner = true
    }
  }

  protected async onScroll() {
    await this.initDataIfInViewport()

    // Check if the image event is not needed anymore
    if (this.dataLoaded && !this.scope.showSpinner)
      // Remove event listeners as soon as the product data is loaded
      window.removeEventListener('scroll', this.onScroll)
  }

  /** Called if the parent slide (if this custom element is in a slider) was changed */
  protected async onSlide() {
    await this.initDataIfInViewport()
  }

  protected async onSidebarStateChanged(event: CustomEventInit<SidebarState>) {
    if (event.detail !== 'hidden') {
      await this.initDataIfInViewport()
    }
  }

  protected async onMouseOver(/*event: MouseEvent | TouchEvent*/) {
    await this.updateData()
  }

  protected addEventListeners() {
    this.removeEventListeners()
    // If card product itself resizes
    window.addEventListener('resize', this.onResize, { passive: true })

    if (!this.dataLoaded && !this.dataIsLoading) {
      window.addEventListener('scroll', this.onScroll, { passive: true })
      this.parentSlideshow?.addEventListener('scrollended', this.onSlide)
      this.parentSidebar?.addEventListener(
        'state-changed',
        this.onSidebarStateChanged,
      )
    }

    this.addEventListener('mouseover', this.onMouseOver, { passive: true })
    this.addEventListener('touchstart', this.onMouseOver, { passive: true })

    this.childVariantSelector?.addEventListener(
      'change',
      this.onVariantChanged as EventListener,
    )
  }

  protected removeEventListeners() {
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('scroll', this.onScroll)

    this.parentSlideshow?.removeEventListener('scrollended', this.onSlide)
    this.parentSidebar?.removeEventListener(
      'state-changed',
      this.onSidebarStateChanged,
    )

    this.removeEventListener('mouseover', this.onMouseOver)
    this.removeEventListener('touchstart', this.onMouseOver)

    this.childVariantSelector?.removeEventListener(
      'change',
      this.onVariantChanged as EventListener,
    )
  }

  protected template(): ReturnType<TemplateFunction> {
    const currentTemplate = this.innerHTML

    // This is the original element
    if (currentTemplate.includes('rv-element="featuredImageEl"')) {
      const tmp = document.createElement('template')
      tmp.content.textContent = currentTemplate
    }

    return null
  }
}
